import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import {
  StrategyDailyMetric,
  TossDataRequest,
  DeployMarkerRequest,
  DeployMarkerItem,
  BannerPositionInput,
} from '../../../types/Ad.api.type';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import toast from 'react-hot-toast';

const POSITION_LABELS: Record<string, string> = {
  MAIN_FEED: '메인 피드',
  MAIN_FEED_TOP: '메인 피드 상단',
  POPULAR_FEED: '인기설문 피드',
  POPULAR_FEED_TOP: '인기설문 피드 상단',
  MY_FEED: '내설문 피드',
  MY_FEED_TOP: '내설문 피드 상단',
  DETAIL_COMMENT: '상세 댓글 위',
  ATTENDANCE: '출석 달력 하단',
  BENEFIT: '혜택 페이지',
  MAIN_FEED_IMAGE: '메인 피드 이미지',
  OTHER: '기타 (미분리)',
};

const PAGE_SIZE = 14;

const getDefaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 60); // API는 넉넉하게 60일
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

const getDefaultFilter = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 14);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
};

export const TossDataInputTab = () => {
  const defaultRange = getDefaultRange();
  const defaultFilter = getDefaultFilter();
  const [form, setForm] = useState<TossDataRequest>({ metricDate: '' });
  const [deployForm, setDeployForm] = useState<DeployMarkerRequest>({ metricDate: '', deployVersion: '' });
  const [filterFrom, setFilterFrom] = useState(defaultFilter.from);
  const [filterTo, setFilterTo] = useState(defaultFilter.to);
  const queryClient = useQueryClient();

  const { data: metricsData } = useQuery({
    queryKey: ['strategyMetrics', defaultRange.from, defaultRange.to],
    queryFn: () => adApi.getStrategyMetrics(defaultRange.from, defaultRange.to).then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const { data: deploys } = useQuery<DeployMarkerItem[]>({
    queryKey: ['strategyDeploys'],
    queryFn: () => adApi.getDeploys().then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const { data: positionConfigs } = useQuery({
    queryKey: ['positionConfigs'],
    queryFn: () => adApi.getPositionConfigs().then((res) => res.data),
    meta: { hideGlobalLoader: true },
  });

  const metrics = useMemo(() => metricsData?.data ?? [], [metricsData]);
  const tossEnteredMetrics = useMemo(
    () =>
      metrics.filter(
        (m) =>
          m.tossRewardImpressions != null ||
          m.tossBannerImpressions != null ||
          m.tossImageImpressions != null ||
          m.tossInterstitialImpressions != null ||
          m.newUsers != null,
      ),
    [metrics],
  );

  // 날짜 필터 적용
  const filteredMetrics = useMemo(
    () => tossEnteredMetrics.filter((m) => m.date >= filterFrom && m.date <= filterTo),
    [tossEnteredMetrics, filterFrom, filterTo],
  );

  // 최근 14건만 표시
  const recentMetrics = useMemo(() => {
    return filteredMetrics.slice(-PAGE_SIZE);
  }, [filteredMetrics]);

  // 토스 배너 활성 위치 목록
  const enabledBannerPositions = useMemo(
    () => (positionConfigs ?? []).filter((c) => c.isTossAdEnabled).map((c) => c.position),
    [positionConfigs],
  );

  // 토스 이미지배너 활성 위치 목록
  const enabledImagePositions = useMemo(
    () => (positionConfigs ?? []).filter((c) => c.isTossImageAdEnabled).map((c) => c.position),
    [positionConfigs],
  );

  const tossMutation = useMutation({
    mutationFn: (data: TossDataRequest) => adApi.saveTossData(data),
    meta: { hideGlobalLoader: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategyMetrics'] });
      toast.success('토스 데이터가 저장되었습니다.');
      setForm({ metricDate: '' });
    },
    onError: () => toast.error('저장 실패'),
  });

  const deployMutation = useMutation({
    mutationFn: (data: DeployMarkerRequest) => adApi.saveDeployMarker(data),
    meta: { hideGlobalLoader: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strategyMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['strategyDeploys'] });
      toast.success('배포 마커가 등록되었습니다.');
      setDeployForm({ metricDate: '', deployVersion: '' });
    },
    onError: () => toast.error('저장 실패'),
  });

  const handleTossSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.metricDate) return toast.error('날짜를 선택하세요');
    tossMutation.mutate(form);
  };

  const handleDeploySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!deployForm.metricDate || !deployForm.deployVersion) return toast.error('날짜와 버전을 입력하세요');
    deployMutation.mutate(deployForm);
  };

  const handleRowClick = (metric: StrategyDailyMetric) => {
    // 기본/꺼내기 분리 데이터가 있으면 그대로 사용, 없으면 기존 합산을 기본 리워드로 표시
    const hasRewardSplit = metric.tossRewardDefaultImpressions != null || metric.tossRewardPulloutImpressions != null;
    const base: TossDataRequest = {
      metricDate: metric.date,
      newUsers: metric.newUsers ?? undefined,
      totalUsers: metric.totalUsers ?? undefined,
      retentionD1New: metric.retentionD1New ?? undefined,
      retentionD1Total: metric.retentionD1Total ?? undefined,
      // 기본 리워드
      tossRewardDefaultImpressions: hasRewardSplit
        ? metric.tossRewardDefaultImpressions ?? undefined
        : metric.tossRewardImpressions ?? undefined,
      tossRewardDefaultEcpm: hasRewardSplit
        ? metric.tossRewardDefaultEcpm ?? undefined
        : metric.tossRewardEcpm ?? undefined,
      tossRewardDefaultRevenue: hasRewardSplit
        ? metric.tossRewardDefaultRevenue ?? undefined
        : metric.tossRewardRevenue ?? undefined,
      tossRewardDefaultCtr: hasRewardSplit
        ? metric.tossRewardDefaultCtr ?? undefined
        : metric.tossRewardCtr ?? undefined,
      // 꺼내기 리워드
      tossRewardPulloutImpressions: metric.tossRewardPulloutImpressions ?? undefined,
      tossRewardPulloutEcpm: metric.tossRewardPulloutEcpm ?? undefined,
      tossRewardPulloutRevenue: metric.tossRewardPulloutRevenue ?? undefined,
      tossRewardPulloutCtr: metric.tossRewardPulloutCtr ?? undefined,
      // 전면광고
      tossInterstitialImpressions: metric.tossInterstitialImpressions ?? undefined,
      tossInterstitialEcpm: metric.tossInterstitialEcpm ?? undefined,
      tossInterstitialRevenue: metric.tossInterstitialRevenue ?? undefined,
      tossInterstitialCtr: metric.tossInterstitialCtr ?? undefined,
    };

    if (metric.bannerPositions && metric.bannerPositions.length > 0) {
      base.bannerPositions = metric.bannerPositions.map((bp) => ({
        position: bp.position,
        impressions: bp.impressions ?? undefined,
        ecpm: bp.ecpm ?? undefined,
        revenue: bp.revenue ?? undefined,
        ctr: bp.ctr ?? undefined,
      }));
    } else {
      base.tossBannerImpressions = metric.tossBannerImpressions ?? undefined;
      base.tossBannerEcpm = metric.tossBannerEcpm ?? undefined;
      base.tossBannerRevenue = metric.tossBannerRevenue ?? undefined;
      base.tossBannerCtr = metric.tossBannerCtr ?? undefined;
    }

    if (metric.imagePositions && metric.imagePositions.length > 0) {
      base.imagePositions = metric.imagePositions.map((ip) => ({
        position: ip.position,
        impressions: ip.impressions ?? undefined,
        ecpm: ip.ecpm ?? undefined,
        revenue: ip.revenue ?? undefined,
        ctr: ip.ctr ?? undefined,
      }));
    } else {
      base.tossImageImpressions = metric.tossImageImpressions ?? undefined;
      base.tossImageEcpm = metric.tossImageEcpm ?? undefined;
      base.tossImageRevenue = metric.tossImageRevenue ?? undefined;
      base.tossImageCtr = metric.tossImageCtr ?? undefined;
    }

    setForm(base);
  };

  const updateBannerPosition = (position: string, field: keyof BannerPositionInput, value: number | undefined) => {
    const positions = [...(form.bannerPositions ?? [])];
    const idx = positions.findIndex((p) => p.position === position);
    if (idx >= 0) {
      positions[idx] = { ...positions[idx], [field]: value };
    } else {
      positions.push({ position, [field]: value });
    }
    setForm({ ...form, bannerPositions: positions });
  };

  const getBannerPositionValue = (position: string, field: keyof BannerPositionInput) => {
    return form.bannerPositions?.find((p) => p.position === position)?.[field] as number | undefined;
  };

  const updateImagePosition = (position: string, field: keyof BannerPositionInput, value: number | undefined) => {
    const positions = [...(form.imagePositions ?? [])];
    const idx = positions.findIndex((p) => p.position === position);
    if (idx >= 0) {
      positions[idx] = { ...positions[idx], [field]: value };
    } else {
      positions.push({ position, [field]: value });
    }
    setForm({ ...form, imagePositions: positions });
  };

  const getImagePositionValue = (position: string, field: keyof BannerPositionInput) => {
    return form.imagePositions?.find((p) => p.position === position)?.[field] as number | undefined;
  };

  const numInput = (label: string, value: number | undefined, key: keyof TossDataRequest, step?: string) => (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input
        type="number"
        step={step}
        value={value ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value ? Number(e.target.value) : undefined })}
        className="w-full border rounded px-2 py-1.5 text-sm"
      />
    </div>
  );

  const bannerNumInput = (label: string, position: string, field: keyof BannerPositionInput, step?: string) => (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input
        type="number"
        step={step}
        value={getBannerPositionValue(position, field) ?? ''}
        onChange={(e) => updateBannerPosition(position, field, e.target.value ? Number(e.target.value) : undefined)}
        className="w-full border rounded px-2 py-1.5 text-sm"
      />
    </div>
  );

  const imageNumInput = (label: string, position: string, field: keyof BannerPositionInput, step?: string) => (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input
        type="number"
        step={step}
        value={getImagePositionValue(position, field) ?? ''}
        onChange={(e) => updateImagePosition(position, field, e.target.value ? Number(e.target.value) : undefined)}
        className="w-full border rounded px-2 py-1.5 text-sm"
      />
    </div>
  );

  // 위치별 입력 모드 사용 여부
  const usePositionMode = enabledBannerPositions.length > 0;
  const useImagePositionMode = enabledImagePositions.length > 0;

  return (
    <div className="space-y-6">
      {/* 토스 데이터 입력 폼 */}
      <Card>
        <form onSubmit={handleTossSubmit} className="p-4 space-y-4">
          <h4 className="text-sm font-bold text-gray-900">토스 데이터 입력</h4>

          {/* 날짜 */}
          <div className="max-w-[200px]">
            <label className="text-xs text-gray-500">날짜 *</label>
            <input
              type="date"
              required
              value={form.metricDate}
              onChange={(e) => setForm({ ...form, metricDate: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>

          {/* 섹션1: 기본 리워드 (일별) */}
          <div>
            <p className="text-xs font-semibold text-blue-600 mb-2">기본 리워드 (일별)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {numInput('노출', form.tossRewardDefaultImpressions, 'tossRewardDefaultImpressions')}
              {numInput('시청률 (%)', form.tossRewardDefaultCtr, 'tossRewardDefaultCtr', '0.01')}
              {numInput('eCPM (원)', form.tossRewardDefaultEcpm, 'tossRewardDefaultEcpm')}
              {numInput('수익 (원)', form.tossRewardDefaultRevenue, 'tossRewardDefaultRevenue')}
            </div>
          </div>

          {/* 섹션1-2: 꺼내기 리워드 (일별) */}
          <div>
            <p className="text-xs font-semibold text-indigo-600 mb-2">꺼내기 리워드 (일별)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {numInput('노출', form.tossRewardPulloutImpressions, 'tossRewardPulloutImpressions')}
              {numInput('시청률 (%)', form.tossRewardPulloutCtr, 'tossRewardPulloutCtr', '0.01')}
              {numInput('eCPM (원)', form.tossRewardPulloutEcpm, 'tossRewardPulloutEcpm')}
              {numInput('수익 (원)', form.tossRewardPulloutRevenue, 'tossRewardPulloutRevenue')}
            </div>
          </div>

          {/* 섹션2: 배너 광고 */}
          <div>
            <p className="text-xs font-semibold text-orange-600 mb-2">
              배너 광고 {usePositionMode ? '(위치별)' : '(일별)'}
            </p>

            {usePositionMode ? (
              <div className="space-y-3">
                {/* 분리된 위치들 */}
                {enabledBannerPositions.map((position) => (
                  <div key={position} className="border-l-2 border-orange-200 pl-3">
                    <p className="text-xs font-medium text-orange-500 mb-1">{POSITION_LABELS[position] ?? position}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {bannerNumInput('노출', position, 'impressions')}
                      {bannerNumInput('시청률 (%)', position, 'ctr', '0.01')}
                      {bannerNumInput('eCPM (원)', position, 'ecpm')}
                      {bannerNumInput('수익 (원)', position, 'revenue')}
                    </div>
                  </div>
                ))}

                {/* 기타 (미분리) */}
                <div className="border-l-2 border-gray-300 pl-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">기타 (미분리)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {bannerNumInput('노출', 'OTHER', 'impressions')}
                    {bannerNumInput('시청률 (%)', 'OTHER', 'ctr', '0.01')}
                    {bannerNumInput('eCPM (원)', 'OTHER', 'ecpm')}
                    {bannerNumInput('수익 (원)', 'OTHER', 'revenue')}
                  </div>
                </div>
              </div>
            ) : (
              /* 레거시 모드: 합산 입력 */
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {numInput('노출', form.tossBannerImpressions, 'tossBannerImpressions')}
                {numInput('시청률 (%)', form.tossBannerCtr, 'tossBannerCtr', '0.01')}
                {numInput('eCPM (원)', form.tossBannerEcpm, 'tossBannerEcpm')}
                {numInput('수익 (원)', form.tossBannerRevenue, 'tossBannerRevenue')}
              </div>
            )}
          </div>

          {/* 섹션3: 이미지배너 광고 */}
          <div>
            <p className="text-xs font-semibold text-emerald-600 mb-2">
              이미지배너 광고 {useImagePositionMode ? '(위치별)' : '(일별)'}
            </p>

            {useImagePositionMode ? (
              <div className="space-y-3">
                {enabledImagePositions.map((position) => (
                  <div key={position} className="border-l-2 border-emerald-200 pl-3">
                    <p className="text-xs font-medium text-emerald-500 mb-1">{POSITION_LABELS[position] ?? position}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {imageNumInput('노출', position, 'impressions')}
                      {imageNumInput('시청률 (%)', position, 'ctr', '0.01')}
                      {imageNumInput('eCPM (원)', position, 'ecpm')}
                      {imageNumInput('수익 (원)', position, 'revenue')}
                    </div>
                  </div>
                ))}

                <div className="border-l-2 border-gray-300 pl-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">기타 (미분리)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {imageNumInput('노출', 'OTHER', 'impressions')}
                    {imageNumInput('시청률 (%)', 'OTHER', 'ctr', '0.01')}
                    {imageNumInput('eCPM (원)', 'OTHER', 'ecpm')}
                    {imageNumInput('수익 (원)', 'OTHER', 'revenue')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {numInput('노출', form.tossImageImpressions, 'tossImageImpressions')}
                {numInput('시청률 (%)', form.tossImageCtr, 'tossImageCtr', '0.01')}
                {numInput('eCPM (원)', form.tossImageEcpm, 'tossImageEcpm')}
                {numInput('수익 (원)', form.tossImageRevenue, 'tossImageRevenue')}
              </div>
            )}
          </div>

          {/* 섹션4: 전면광고 (일별) */}
          <div>
            <p className="text-xs font-semibold text-rose-600 mb-2">전면광고 (일별)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {numInput('노출', form.tossInterstitialImpressions, 'tossInterstitialImpressions')}
              {numInput('시청률 (%)', form.tossInterstitialCtr, 'tossInterstitialCtr', '0.01')}
              {numInput('eCPM (원)', form.tossInterstitialEcpm, 'tossInterstitialEcpm')}
              {numInput('수익 (원)', form.tossInterstitialRevenue, 'tossInterstitialRevenue')}
            </div>
          </div>

          {/* 섹션5: 유저 / 리텐션 */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">유저 / 리텐션</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {numInput('신규유저', form.newUsers, 'newUsers')}
              {numInput('전체유저', form.totalUsers, 'totalUsers')}
              {numInput('D1 리텐션 신규 (%)', form.retentionD1New, 'retentionD1New', '0.1')}
              {numInput('D1 리텐션 기존 (%)', form.retentionD1Total, 'retentionD1Total', '0.1')}
            </div>
          </div>

          <Button type="submit" size="sm" disabled={tossMutation.isPending}>
            {tossMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </form>
      </Card>

      {/* 입력된 데이터 목록 */}
      {tossEnteredMetrics.length > 0 && (
        <Card>
          <div className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <h4 className="text-sm font-semibold text-gray-700">
                {`입력된 토스 데이터 (${filteredMetrics.length}건)`}
              </h4>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={filterFrom}
                  onChange={(e) => {
                    setFilterFrom(e.target.value);
                  }}
                  className="h-7 px-2 text-xs border rounded bg-white"
                />
                <span className="text-gray-400 text-xs">~</span>
                <input
                  type="date"
                  value={filterTo}
                  onChange={(e) => {
                    setFilterTo(e.target.value);
                  }}
                  className="h-7 px-2 text-xs border rounded bg-white"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-2 whitespace-nowrap">날짜</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-blue-500">R노출</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-blue-500">R시청</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-blue-500">R-eCPM</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-blue-500 font-bold">R수익</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-orange-500">B노출</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-orange-500">B시청</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-orange-500">B-eCPM</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-orange-500 font-bold">B수익</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-emerald-500">I노출</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-emerald-500">I시청</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-emerald-500">I-eCPM</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-emerald-500 font-bold">I수익</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-rose-500">F노출</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-rose-500">F시청</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-rose-500">F-eCPM</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap text-rose-500 font-bold">F수익</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap font-bold">총수익</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap">신규</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap">전체</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap">D1신규</th>
                    <th className="pb-2 pr-2 text-right whitespace-nowrap">D1기존</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMetrics.map((d) => (
                    <tr
                      key={d.date}
                      className="border-b last:border-0 hover:bg-blue-50 cursor-pointer"
                      onClick={() => handleRowClick(d)}
                    >
                      <td className="py-1.5 pr-2 font-mono whitespace-nowrap">
                        {d.date.slice(5)}
                        {d.bannerPositions && d.bannerPositions.length > 0 && (
                          <span className="ml-1 text-orange-400 text-[9px]" title="배너 위치별 데이터">
                            ●
                          </span>
                        )}
                        {d.imagePositions && d.imagePositions.length > 0 && (
                          <span className="ml-0.5 text-emerald-400 text-[9px]" title="이미지 위치별 데이터">
                            ●
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossRewardImpressions ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.tossRewardCtr != null ? `${d.tossRewardCtr}%` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossRewardEcpm ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-bold">{d.tossRewardRevenue ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossBannerImpressions ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.tossBannerCtr != null ? `${d.tossBannerCtr}%` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossBannerEcpm ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-bold">{d.tossBannerRevenue ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossImageImpressions ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.tossImageCtr != null ? `${d.tossImageCtr}%` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossImageEcpm ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-bold">{d.tossImageRevenue ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossInterstitialImpressions ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.tossInterstitialCtr != null ? `${d.tossInterstitialCtr}%` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.tossInterstitialEcpm ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-bold">{d.tossInterstitialRevenue ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono font-bold">
                        {(() => {
                          const total =
                            (d.tossRewardRevenue ?? 0) +
                            (d.tossBannerRevenue ?? 0) +
                            (d.tossImageRevenue ?? 0) +
                            (d.tossInterstitialRevenue ?? 0);
                          return total > 0 ? total.toLocaleString() : '-';
                        })()}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.newUsers ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{d.totalUsers ?? '-'}</td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.retentionD1New != null ? `${d.retentionD1New}%` : '-'}
                      </td>
                      <td className="py-1.5 pr-2 text-right font-mono">
                        {d.retentionD1Total != null ? `${d.retentionD1Total}%` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* 배포 마커 등록 */}
      <Card>
        <form onSubmit={handleDeploySubmit} className="p-4 space-y-3">
          <h4 className="text-sm font-bold text-gray-900">배포 마커 등록</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500">배포 날짜 *</label>
              <input
                type="date"
                required
                value={deployForm.metricDate}
                onChange={(e) => setDeployForm({ ...deployForm, metricDate: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">버전 *</label>
              <input
                type="text"
                required
                placeholder="v1.5.0"
                value={deployForm.deployVersion}
                onChange={(e) => setDeployForm({ ...deployForm, deployVersion: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">메모</label>
              <input
                type="text"
                placeholder="주요 변경사항"
                value={deployForm.deployNote ?? ''}
                onChange={(e) => setDeployForm({ ...deployForm, deployNote: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <Button type="submit" size="sm" disabled={deployMutation.isPending}>
            {deployMutation.isPending ? '저장 중...' : '등록'}
          </Button>
        </form>
      </Card>

      {/* 등록된 배포 마커 목록 */}
      <Card>
        <div className="p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">등록된 배포 마커</h4>
          {!deploys || deploys.length === 0 ? (
            <p className="text-sm text-gray-400">등록된 배포 마커가 없습니다.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-4">날짜</th>
                    <th className="pb-2 pr-4">버전</th>
                    <th className="pb-2">메모</th>
                  </tr>
                </thead>
                <tbody>
                  {deploys.map((d: DeployMarkerItem) => (
                    <tr key={d.date} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 pr-4 font-mono text-gray-600">{d.date}</td>
                      <td className="py-2 pr-4">
                        <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded">{d.version}</span>
                      </td>
                      <td className="py-2 text-gray-500">{d.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
